/* The gangsheet canvas (Konva). World units are millimetres; the stage
   scale is screen pixels per millimetre. The printable film is a
   checkerboard (transparent) on a darker backdrop, with rulers, the edge
   guide, snapping guides with the gap, and a badge on anything to check. */

import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Stage, Layer, Rect, Group, Image as KImage, Transformer, Line, Text, Circle, Shape } from 'react-konva';
import type Konva from 'konva';
import { itemBox, formatDims, formatLength, effectiveDpi, layoutGap, type Item, type Sheet, type Design, type AssetInfo } from '@gsb/core';
import { useStore, select, toggleSelect, clearSelection, commit, clampInside, fitInside, addCopy } from '../store/store';
import { useAssetImage, checkerTile, preload } from './images';
import { Icon } from '../ui/icons';
import { Rulers, RULER } from './Rulers';
import { useMedia } from '../ui/files';
import { SelectionOverlay, SelectionMenu, type PxRect } from '../workspace/SelectionTools';

export interface View { scale: number; x: number; y: number }
export const PX_PER_MM_100 = 96 / 25.4;       // "100%" = real size on a 96 DPI screen

export interface CanvasApi {
  zoomBy: (f: number) => void;
  fit: () => void;
  actual: () => void;
  view: View;
}

interface Props {
  sheetIndex: number;
  mode: 'edit' | 'view';
  background?: 'checker' | 'dark' | 'white';
  flagged?: Map<string, 'error' | 'warning'>;
  /** where printed artwork is too close or overlaps (sheet mm), shown as small rings */
  spots?: Array<{ x: number; y: number; level: 'error' | 'warning' }>;
  onApi?: (api: CanvasApi) => void;
  onView?: (v: View) => void;
  showMinimap?: boolean;
  /** print preview: no guides, just the film and the artwork */
  clean?: boolean;
  /** change it to show the whole sheet again once the canvas has its new size (Focus mode) */
  refitKey?: unknown;
  /** rulers along the top and left */
  rulers?: boolean;
  /** dashed edge-margin guide */
  edgeGuide?: boolean;
  /** drag anywhere to move around (no selecting or moving designs) */
  panMode?: boolean;
}

const SNAP_PX = 7;
interface Box { x: number; y: number; width: number; height: number; rotation: number }

export function SheetCanvas({ sheetIndex, mode, background = 'checker', flagged, spots, onApi, onView, showMinimap = true, clean = false, rulers = false, edgeGuide = true, panMode = false, refitKey }: Props) {
  const wrap = useRef<HTMLDivElement>(null);
  const stage = useRef<Konva.Stage>(null);
  const tr = useRef<Konva.Transformer>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const sheet = useStore(s => s.doc.sheets[sheetIndex]) as Sheet | undefined;
  const designs = useStore(s => s.doc.designs);
  const assets = useStore(s => s.assets);
  const selection = useStore(s => s.selection);
  const snap = useStore(s => s.snap);
  const lockAspect = useStore(s => s.lockAspect);
  // the gap Auto nest keeps (the customer's choice, never under the shop's minimums)
  const gapMm = useStore(s => (s.config ? layoutGap(s.doc, s.config.builder) : s.doc.settings.gapMm));
  const unit = useStore(s => s.doc.settings.unit);
  const edgeMm = useStore(s => s.config?.builder.edgeMm ?? 5);
  const builder = useStore(s => s.config?.builder);
  const maxLen = useStore(s => s.config?.pricing.transfers[s.doc.transfer].byLength.maxLengthMm ?? 5000);
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
  const [guides, setGuides] = useState<{ v: number[]; h: number[]; label?: { x: number; y: number; text: string } }>({ v: [], h: [] });
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [hud, setHud] = useState<string | null>(null);
  const [panning, setPanning] = useState(false);
  const space = useRef(false);
  /** Space is held over the canvas: the hand cursor shows everywhere on it (0.3e). Kept in React state, not
      just a class added by hand, so a re-render (e.g. when a pan starts or ends) can't drop it. */
  const [spaceHeld, setSpaceHeld] = useState(false);
  /** a copy being moved or resized/turned: the size label follows it live and the toolbar steps aside (0.3e) */
  const [moving, setMoving] = useState<'drag' | 'transform' | null>(null);
  const [live, setLive] = useState<{ rect: PxRect; text?: string } | null>(null);
  /** the right-click / long-press menu, where it was opened (window pixels) */
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const phone = useMedia('(max-width: 759px)');
  /** a finger held still on a design: after a moment, its actions (the touch version of right-click) */
  const press = useRef<{ id: string; x: number; y: number; timer: number } | null>(null);
  const lastLongPress = useRef(0);
  /** keys held while dragging: Shift (other proportion rule), Alt (no snapping) */
  const keys = useRef({ shift: false, alt: false });
  /** the edge margin shows while something is moved or resized close to it */
  const [edgeFlash, setEdgeFlash] = useState(false);
  const fitted = useRef<string>('');
  const designMap = useMemo(() => new Map(designs.map(d => [d.id, d])), [designs]);
  /** touch screens get bigger handles */
  const coarse = useMemo(() => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches, []);

  /* ---------------------------------------------------------------- size */
  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  const fitView = useCallback((whole = true): View | null => {
    if (!sheet || !size.w || !size.h) return null;
    const r = rulers ? RULER : 0;
    const pad = size.w < 600 ? 24 : 40;
    // leave room for the sheet chip and the width label above, and the zoom bar below
    const top = clean ? pad : pad + 40, bot = clean ? pad : pad + 40;
    const availW = Math.max(40, size.w - r - pad * 2), availH = Math.max(40, size.h - r - top - bot);
    const sw = availW / sheet.widthMm;
    const sh = availH / sheet.lengthMm;
    let scale = whole ? Math.min(sw, sh) : sw;
    // a very long sheet: fit the width instead, top aligned
    if (whole && sh < sw * 0.45) scale = Math.min(sw, availH / Math.min(sheet.lengthMm, sheet.widthMm * 1.6));
    scale = Math.max(0.02, Math.min(scale, 40));
    const x = r + pad + (availW - sheet.widthMm * scale) / 2;
    const y = sheet.lengthMm * scale < availH ? r + top + (availH - sheet.lengthMm * scale) / 2 : r + top;
    return { scale, x, y };
  }, [sheet?.widthMm, sheet?.lengthMm, size.w, size.h, rulers, clean]);

  // when a panel opens or closes the canvas changes size: keep the sheet where it was, centred.
  // After `refitKey` changes (Focus mode in or out) the next new size shows the whole sheet instead.
  const lastSize = useRef(size);
  const fitRef = useRef(fitView);
  fitRef.current = fitView;
  const pendingFit = useRef(false);
  const lastRefit = useRef(refitKey);
  useEffect(() => {
    if (lastRefit.current === refitKey) return;
    lastRefit.current = refitKey;
    pendingFit.current = true;
    // if the size doesn't change after all, fit anyway
    const t = window.setTimeout(() => { if (pendingFit.current) { pendingFit.current = false; const v = fitRef.current(); if (v) setView(v); } }, 250);
    return () => window.clearTimeout(t);
  }, [refitKey]);
  useEffect(() => {
    const prev = lastSize.current;
    lastSize.current = size;
    if (mode !== 'edit' || !prev.w || !size.w || (prev.w === size.w && prev.h === size.h)) return;
    if (pendingFit.current) { pendingFit.current = false; const v = fitRef.current(); if (v) { setView(v); return; } }
    const dx = (size.w - prev.w) / 2, dy = (size.h - prev.h) / 2;
    setView(v => ({ ...v, x: v.x + dx, y: v.y + dy }));
  }, [size.w, size.h, mode]);

  useEffect(() => {
    if (!sheet || !size.w) return;
    const key = sheet.id + ':' + Math.round(size.w / 40) + ':' + Math.round(size.h / 40);
    if (fitted.current === key) return;
    const firstForSheet = !fitted.current.startsWith(sheet.id + ':');
    fitted.current = key;
    if (firstForSheet || mode === 'view') { const v = fitView(); if (v) setView(v); }
  }, [sheet?.id, size.w, size.h, fitView, mode]);

  useEffect(() => { onView?.(view); }, [view]);
  const viewRef = useRef(view);
  viewRef.current = view;

  const zoomAt = useCallback((factor: number, px?: number, py?: number) => {
    setView(v => {
      const s = Math.max(0.03, Math.min(60, v.scale * factor));
      const cx = px ?? size.w / 2, cy = py ?? size.h / 2;
      const wx = (cx - v.x) / v.scale, wy = (cy - v.y) / v.scale;
      return { scale: s, x: cx - wx * s, y: cy - wy * s };
    });
  }, [size.w, size.h]);

  useEffect(() => {
    onApi?.({
      zoomBy: f => zoomAt(f),
      fit: () => { const v = fitView(); if (v) setView(v); },
      actual: () => setView(v => {
        const s = PX_PER_MM_100;
        const wx = (size.w / 2 - v.x) / v.scale, wy = (size.h / 2 - v.y) / v.scale;
        return { scale: s, x: size.w / 2 - wx * s, y: size.h / 2 - wy * s };
      }),
      view
    });
  }, [zoomAt, fitView, view, size.w, size.h]);

  /* --------------------------------------------------------- wheel / keys */
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) {
        const f = Math.pow(1.0018, -e.deltaY * (e.deltaMode === 1 ? 30 : 1));
        zoomAt(f, e.clientX - r.left, e.clientY - r.top);
      } else {
        const k = e.deltaMode === 1 ? 30 : 1;
        setView(v => ({ ...v, x: v.x - (e.shiftKey ? e.deltaY : e.deltaX) * k, y: v.y - (e.shiftKey ? 0 : e.deltaY) * k }));
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    /* Space pans while the pointer is over the canvas. Then it must not also "press" the button that
       still has focus (Fit, a rail tool…): that button lets go of the focus. Elsewhere Space still
       presses buttons for keyboard users. */
    let over = false;
    const enter = () => { over = true; }, leave = () => { over = false; };
    const kd = (e: KeyboardEvent) => {
      keys.current = { shift: e.shiftKey, alt: e.altKey };
      if (e.code !== 'Space' || isTyping(e)) return;
      if (!over && !space.current) return;
      if ((document.activeElement as HTMLElement | null)?.closest?.('.sel-ui')) return;   // a toolbar button: Space presses it
      space.current = true;
      setSpaceHeld(true);
      e.preventDefault();
      const f = document.activeElement as HTMLElement | null;
      if (f && f !== document.body && (f.tagName === 'BUTTON' || f.tagName === 'A' || f.getAttribute('role') === 'button')) f.blur();
    };
    const ku = (e: KeyboardEvent) => {
      keys.current = { shift: e.shiftKey, alt: e.altKey };
      if (e.code === 'Space') { space.current = false; setSpaceHeld(false); }
    };
    const blur = () => { keys.current = { shift: false, alt: false }; space.current = false; setSpaceHeld(false); };
    /* Space + drag (or the middle button) pans from ANYWHERE, designs included: the press is caught here,
       before the canvas sees it, so a design under the pointer is never picked up or moved. */
    let held: { id: number; x: number; y: number; vx: number; vy: number } | null = null;
    const down = (e: PointerEvent) => {
      if (!(space.current || (e.pointerType === 'mouse' && e.button === 1))) return;
      if ((e.target as Element | null)?.closest?.('.sel-ui')) return;     // the quick toolbar is for clicking
      e.preventDefault();
      e.stopPropagation();
      held = { id: e.pointerId, x: e.clientX, y: e.clientY, vx: viewRef.current.x, vy: viewRef.current.y };
      setPanning(true);
      try { el.setPointerCapture(e.pointerId); } catch { /* not supported */ }
    };
    const move = (e: PointerEvent) => {
      if (!held || e.pointerId !== held.id) return;
      e.stopPropagation();
      const h = held;
      setView(v => ({ ...v, x: h.vx + (e.clientX - h.x), y: h.vy + (e.clientY - h.y) }));
    };
    const up = (e: PointerEvent) => {
      if (!held || e.pointerId !== held.id) return;
      e.stopPropagation();
      held = null;
      setPanning(false);
      try { el.releasePointerCapture(e.pointerId); } catch { /* fine */ }
    };
    // the mouse events that follow a caught press must not reach the canvas either
    const swallow = (e: MouseEvent) => { if (held || space.current || e.button === 1) { e.stopPropagation(); if (e.button === 1) e.preventDefault(); } };
    el.addEventListener('pointerenter', enter);
    el.addEventListener('pointerleave', leave);
    el.addEventListener('pointerdown', down, true);
    el.addEventListener('pointermove', move, true);
    el.addEventListener('pointerup', up, true);
    el.addEventListener('pointercancel', up, true);
    el.addEventListener('mousedown', swallow, true);
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    window.addEventListener('blur', blur);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', down, true); el.removeEventListener('pointermove', move, true);
      el.removeEventListener('pointerup', up, true); el.removeEventListener('pointercancel', up, true);
      el.removeEventListener('mousedown', swallow, true);
      el.removeEventListener('pointerenter', enter); el.removeEventListener('pointerleave', leave);
      window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); window.removeEventListener('blur', blur);
    };
  }, [zoomAt]);

  /* ---------------------------------------------------- transformer nodes */
  const selOnSheet = useMemo(() => (sheet ? sheet.items.filter(i => selection.includes(i.id)) : []), [sheet, selection]);
  useEffect(() => {
    const t = tr.current, st = stage.current;
    if (!t || !st) return;
    const nodes = selOnSheet.filter(i => !i.locked).map(i => st.findOne('#' + cssId(i.id))).filter(Boolean) as Konva.Node[];
    t.nodes(mode === 'edit' ? nodes : []);
    t.getLayer()?.batchDraw();
  }, [selOnSheet, mode, sheet]);

  /* where the rotate handle is on screen, so the size label and the toolbar keep off it (it's below the
     outline for a copy turned 180°). Read from the transformer once it has its nodes. */
  const [rotPt, setRotPt] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const r = tr.current?.findOne('.rotater');
    const p = r && tr.current!.nodes().length && r.isVisible() ? r.getAbsolutePosition() : null;
    const next = p ? { x: Math.round(p.x), y: Math.round(p.y) } : null;
    setRotPt(v => (v?.x === next?.x && v?.y === next?.y ? v : next));
  }, [selOnSheet, mode, sheet, view, size, panMode]);

  const selBox = useMemo(() => {
    if (!selOnSheet.length) return null;
    const bs = selOnSheet.map(itemBox);
    const x0 = Math.min(...bs.map(b => b.x)), y0 = Math.min(...bs.map(b => b.y));
    return { x: x0, y: y0, w: Math.max(...bs.map(b => b.x + b.w)) - x0, h: Math.max(...bs.map(b => b.y + b.h)) - y0 };
  }, [selOnSheet]);

  /* ------------------------------------------------------- drag (multi) */
  const drag = useRef<{ ids: string[]; start: Map<string, { x: number; y: number }>; box: { x: number; y: number; w: number; h: number } } | null>(null);

  const onItemDragStart = (id: string, e: Konva.KonvaEventObject<DragEvent>) => {
    if (!sheet) return;
    let ids = selection.includes(id) ? selection : [id];
    if (!selection.includes(id)) select([id]);
    ids = ids.filter(x => !sheet.items.find(i => i.id === x)?.locked);
    const st = stage.current!;
    const start = new Map<string, { x: number; y: number }>();
    ids.forEach(x => { const n = st.findOne('#' + cssId(x)); if (n) start.set(x, { x: n.x(), y: n.y() }); });
    const boxes = sheet.items.filter(i => ids.includes(i.id)).map(itemBox);
    const x0 = Math.min(...boxes.map(b => b.x)), y0 = Math.min(...boxes.map(b => b.y));
    const x1 = Math.max(...boxes.map(b => b.x + b.w)), y1 = Math.max(...boxes.map(b => b.y + b.h));
    drag.current = { ids, start, box: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } };
    e.target.moveToTop();
    setMoving('drag');
  };

  const onItemDragMove = (id: string, e: Konva.KonvaEventObject<DragEvent>) => {
    const d = drag.current;
    if (!d || !sheet) return;
    const s0 = d.start.get(id);
    if (!s0) return;
    let dx = e.target.x() - s0.x, dy = e.target.y() - s0.y;
    const b = { ...d.box, x: d.box.x + dx, y: d.box.y + dy };
    // snapping (hold Alt to place freely)
    const s = keys.current.alt ? null : snapBox(b, ['x0', 'xc', 'x1'], ['y0', 'yc', 'y1'], d.ids);
    if (s) { dx += s.dx; dy += s.dy; b.x += s.dx; b.y += s.dy; }
    // never into the edge margin (a roll can grow down to its maximum)
    const L = sheet.kind === 'roll' ? maxLen : sheet.lengthMm;
    if (b.w <= sheet.widthMm - 2 * edgeMm) { const cx = Math.min(Math.max(b.x, edgeMm), sheet.widthMm - edgeMm - b.w); dx += cx - b.x; b.x = cx; }
    if (b.h <= L - 2 * edgeMm) { const cy = Math.min(Math.max(b.y, edgeMm), L - edgeMm - b.h); dy += cy - b.y; b.y = cy; }
    const st = stage.current!;
    d.start.forEach((p, x) => { const n = st.findOne('#' + cssId(x)); n?.position({ x: p.x + dx, y: p.y + dy }); });
    setGuides(s ? s.guides : { v: [], h: [] });
    flashEdge(b, L);
    setHud(formatLength(b.x, unit) + ', ' + formatLength(b.y, unit) + (keys.current.alt ? ' · no snapping' : ''));
    setLive({ rect: { x: view.x + b.x * view.scale, y: view.y + b.y * view.scale, w: b.w * view.scale, h: b.h * view.scale } });
    const p = press.current, ptr = stage.current?.getPointerPosition();
    if (p && ptr) { const r = wrap.current!.getBoundingClientRect(); if (Math.hypot(ptr.x + r.left - p.x, ptr.y + r.top - p.y) > 5) cancelPress(); }
  };

  /** show the edge margin while a box is near it */
  const flashEdge = (b: { x: number; y: number; w: number; h: number }, L: number) => {
    const near = 3 * edgeMm + 6;
    const on = b.x < near || b.y < near || b.x + b.w > sheet!.widthMm - near || (sheet!.kind !== 'roll' && b.y + b.h > L - near);
    setEdgeFlash(v => (v === on ? v : on));
  };

  /**
   * Snap a box's edges (the ones named) to the margins and centre, other designs and the gap next to
   * them, and the grid. Used while moving (all edges) and resizing (the edges being dragged).
   * Returns the offset to apply and the guide lines to draw.
   */
  type Edge = 'x0' | 'xc' | 'x1' | 'y0' | 'yc' | 'y1';
  const snapBox = (b: { x: number; y: number; w: number; h: number }, ex: Edge[], ey: Edge[], skip: string[]) => {
    if (!sheet || !(snap.objects || snap.canvas || snap.grid)) return null;
    const tol = SNAP_PX / view.scale;
    const xs: { v: number; gap?: boolean }[] = [], ys: { v: number; gap?: boolean }[] = [];
    if (snap.canvas) {
      // the printable area's edges and the centre (the film edge itself is out of bounds)
      xs.push({ v: edgeMm }, { v: sheet.widthMm / 2 }, { v: sheet.widthMm - edgeMm });
      ys.push({ v: edgeMm });
      if (sheet.kind !== 'roll') ys.push({ v: sheet.lengthMm / 2 }, { v: sheet.lengthMm - edgeMm });
    }
    if (snap.objects) {
      for (const it of sheet.items) {
        if (skip.includes(it.id)) continue;
        const o = itemBox(it);
        // only bother with things near the box
        if (o.x > b.x + b.w + 200 || o.x + o.w < b.x - 200 || o.y > b.y + b.h + 200 || o.y + o.h < b.y - 200) continue;
        xs.push({ v: o.x }, { v: o.x + o.w / 2 }, { v: o.x + o.w });
        ys.push({ v: o.y }, { v: o.y + o.h / 2 }, { v: o.y + o.h });
        // the gap Auto nest keeps: right of / left of / below / above
        const vOverlap = o.y < b.y + b.h && b.y < o.y + o.h;
        const hOverlap = o.x < b.x + b.w && b.x < o.x + o.w;
        if (vOverlap) xs.push({ v: o.x + o.w + gapMm, gap: true }, { v: o.x - gapMm, gap: true });
        if (hOverlap) ys.push({ v: o.y + o.h + gapMm, gap: true }, { v: o.y - gapMm, gap: true });
      }
    }
    const at = (e: Edge) => (e === 'x0' ? b.x : e === 'xc' ? b.x + b.w / 2 : e === 'x1' ? b.x + b.w : e === 'y0' ? b.y : e === 'yc' ? b.y + b.h / 2 : b.y + b.h);
    const pick = (edges: Edge[], cands: { v: number; gap?: boolean }[]) => {
      let best: { off: number; at: number; gap?: boolean; edge: Edge } | null = null;
      for (const e of edges) for (const c of cands) {
        const off = c.v - at(e);
        if (Math.abs(off) <= tol && (!best || Math.abs(off) < Math.abs(best.off))) best = { off, at: c.v, gap: c.gap, edge: e };
      }
      return best;
    };
    let sx = pick(ex, xs), sy = pick(ey, ys);
    const g = snap.gridMm;
    const gridX = !sx && snap.grid && ex.length ? (() => { const e = ex[0]; const v = at(e); return { off: Math.round(v / g) * g - v, at: NaN, edge: e }; })() : null;
    const gridY = !sy && snap.grid && ey.length ? (() => { const e = ey[0]; const v = at(e); return { off: Math.round(v / g) * g - v, at: NaN, edge: e }; })() : null;
    const dx = sx ? sx.off : gridX ? gridX.off : 0, dy = sy ? sy.off : gridY ? gridY.off : 0;
    let label: { x: number; y: number; text: string } | undefined;
    if (sx?.gap) label = { x: sx.at, y: b.y + dy + b.h / 2, text: gapMm + ' mm' };
    if (sy?.gap) label = { x: b.x + dx + b.w / 2, y: sy.at, text: gapMm + ' mm' };
    return { dx, dy, edgeX: sx?.edge ?? gridX?.edge, edgeY: sy?.edge ?? gridY?.edge, guides: { v: sx ? [sx.at] : [], h: sy ? [sy.at] : [], label } };
  };

  const onItemDragEnd = () => {
    const d = drag.current;
    drag.current = null;
    setGuides({ v: [], h: [] });
    setHud(null);
    setMoving(null);
    setLive(null);
    setEdgeFlash(false);
    if (!d || !sheet) return;
    const st = stage.current!;
    const pos = new Map<string, { x: number; y: number }>();
    d.ids.forEach(x => { const n = st.findOne('#' + cssId(x)); if (n) pos.set(x, { x: n.x(), y: n.y() }); });
    const moved = [...pos.entries()].some(([x, p]) => { const s = d.start.get(x)!; return Math.abs(s.x - p.x) > 1e-6 || Math.abs(s.y - p.y) > 1e-6; });
    if (!moved) return;
    commit(d.ids.length > 1 ? 'Move ' + d.ids.length + ' transfers' : 'Move', doc => {
      const sh = doc.sheets[sheetIndex];
      sh.items.forEach(it => { const p = pos.get(it.id); if (p) { it.cx = p.x; it.cy = p.y; } });
    });
  };

  /* --------------------------------------------------------- transform
     One design: eight handles. Locked proportions (the default): every handle keeps the shape, an edge
     handle growing from the opposite edge. Unlocked: edges stretch one side, corners stretch freely.
     Shift switches the rule while dragging. Several designs: corners only, always in proportion. */
  const single = selOnSheet.filter(i => !i.locked).length === 1;
  const keepRatioNow = () => !single || (lockAspect !== keys.current.shift);
  useEffect(() => {
    const t = tr.current;
    if (!t) return;
    const sync = () => {
      t.keepRatio(keepRatioNow());
      t.rotationSnaps(keys.current.alt ? [] : [0, 90, 180, 270]);
    };
    sync();
    window.addEventListener('keydown', sync);
    window.addEventListener('keyup', sync);
    return () => { window.removeEventListener('keydown', sync); window.removeEventListener('keyup', sync); };
  }, [single, lockAspect]);

  /** Konva's box (stage pixels, may be rotated) → the next box, after the lock, snapping and the margin. */
  const boundBox = (oldBox: Box, newBox: Box): Box => {
    if (newBox.width < 6 || newBox.height < 6) return oldBox;
    const t = tr.current;
    const anchor = t?.getActiveAnchor() || '';
    if (!t || !sheet || !anchor || anchor === 'rotater') return newBox;
    let b = { ...newBox };
    const keep = keepRatioNow();
    const isEdge = anchor === 'middle-left' || anchor === 'middle-right' || anchor === 'top-center' || anchor === 'bottom-center';
    const cos = Math.cos(b.rotation), sin = Math.sin(b.rotation);
    // an edge handle with the lock on: the other side follows, centred on the same line
    if (keep && isEdge && single) {
      const r = oldBox.width / oldBox.height;
      if (anchor === 'middle-left' || anchor === 'middle-right') {
        const nh = b.width / r, dh = nh - b.height;
        b = { ...b, height: nh, x: b.x + sin * dh / 2, y: b.y - cos * dh / 2 };
      } else {
        const nw = b.height * r, dw = nw - b.width;
        b = { ...b, width: nw, x: b.x - cos * dw / 2, y: b.y - sin * dw / 2 };
      }
    }
    // snapping and the printable area, for an unturned design (turned ones are checked when you let go)
    const turned = Math.abs(Math.sin(b.rotation)) > 1e-3 || Math.cos(b.rotation) < 0;
    if (!single || turned || keys.current.alt) { setGuides({ v: [], h: [] }); return b; }
    const it = selOnSheet.find(i => !i.locked)!;
    const sc = view.scale;
    let mm = { x: (b.x - view.x) / sc, y: (b.y - view.y) / sc, w: b.width / sc, h: b.height / sc };
    const L = sheet.kind === 'roll' ? maxLen : sheet.lengthMm;
    const movesL = anchor.includes('left'), movesR = anchor.includes('right'), movesT = anchor.includes('top'), movesB = anchor.includes('bottom');
    const ex: Edge[] = movesL ? ['x0'] : movesR ? ['x1'] : [];
    const ey: Edge[] = movesT ? ['y0'] : movesB ? ['y1'] : [];
    const s = snapBox(mm, ex, ey, [it.id]);
    const ratio = mm.w / mm.h;
    const applyX = (dx: number) => { if (movesL) { mm.x += dx; mm.w -= dx; } else if (movesR) mm.w += dx; };
    const applyY = (dy: number) => { if (movesT) { mm.y += dy; mm.h -= dy; } else if (movesB) mm.h += dy; };
    // with the lock, one axis leads and the other follows (from the fixed corner/edge)
    const follow = (lead: 'x' | 'y') => {
      if (!keep) return;
      if (lead === 'x') { const nh = mm.w / ratio; if (movesT) mm.y += mm.h - nh; else if (!movesB) mm.y -= (nh - mm.h) / 2; mm.h = nh; }
      else { const nw = mm.h * ratio; if (movesL) mm.x += mm.w - nw; else if (!movesR) mm.x -= (nw - mm.w) / 2; mm.w = nw; }
    };
    if (s && (s.dx || s.dy)) {
      if (keep && ex.length && ey.length) {
        // a corner with the lock: snap whichever edge is closer, the other follows
        if (s.edgeX && (!s.edgeY || Math.abs(s.dx) <= Math.abs(s.dy))) { applyX(s.dx); follow('x'); s.guides.h = []; }
        else { applyY(s.dy); follow('y'); s.guides.v = []; }
      } else {
        if (ex.length) { applyX(s.dx); if (keep && !ey.length) follow('x'); }
        if (ey.length) { applyY(s.dy); if (keep && !ex.length) follow('y'); }
      }
      setGuides(s.guides);
    } else setGuides({ v: [], h: [] });
    // the printable area: the dragged edges stop at the margin
    const x0 = edgeMm, x1 = sheet.widthMm - edgeMm, y0 = edgeMm, y1 = L - edgeMm;
    if (movesL && mm.x < x0) { applyX(x0 - mm.x); follow('x'); }
    if (movesR && mm.x + mm.w > x1) { applyX(x1 - (mm.x + mm.w)); follow('x'); }
    if (movesT && mm.y < y0) { applyY(y0 - mm.y); follow('y'); }
    if (movesB && mm.y + mm.h > y1) { applyY(y1 - (mm.y + mm.h)); follow('y'); }
    if (mm.w * sc < 6 || mm.h * sc < 6 || mm.x < x0 - 0.01 || mm.y < y0 - 0.01 || mm.x + mm.w > x1 + 0.01 || mm.y + mm.h > y1 + 0.01) {
      // can't fit this way (e.g. a locked corner hitting two margins): keep the last good box
      if (mm.w * sc < 6 || mm.h * sc < 6) return oldBox;
      mm = { x: Math.max(mm.x, x0), y: Math.max(mm.y, y0), w: Math.min(mm.w, x1 - Math.max(mm.x, x0)), h: Math.min(mm.h, y1 - Math.max(mm.y, y0)) };
      if (keep) return oldBox;
    }
    flashEdge(mm, L);
    return { ...b, x: view.x + mm.x * sc, y: view.y + mm.y * sc, width: mm.w * sc, height: mm.h * sc };
  };

  const onTransform = () => {
    const t = tr.current;
    if (!t || !sheet) return;
    const n = t.nodes()[0];
    if (!n) return;
    const it = sheet.items.find(i => cssId(i.id) === n.id());
    if (!it) return;
    const w = it.widthMm * Math.abs(n.scaleX()), h = it.heightMm * Math.abs(n.scaleY());
    const d = designMap.get(it.designId), a = d && assets[d.assetId];
    const dpi = d && a && builder ? effectiveDpi(a, d.trim, w, h, builder) : null;
    const anchor = t.getActiveAnchor();
    // (the Shift hint is for keyboards: not on touch screens)
    const lockNote = t.nodes().length > 1 || anchor === 'rotater' || coarse ? '' : keepRatioNow() ? ' · Shift to stretch' : ' · Shift to keep proportions';
    const rot = Math.round(((n.rotation() % 360) + 360) % 360);
    const text = t.nodes().length > 1
      ? formatDims(...groupSize(t.nodes()), unit) + ' · ' + t.nodes().length + ' selected'
      : formatDims(w, h, unit) + (rot % 360 || anchor === 'rotater' ? ' · ' + rot + '°' : '') + (dpi && isFinite(dpi.dpi) ? ' · ' + Math.round(dpi.dpi) + ' DPI' : '') + lockNote;
    // the outline on screen (turned copies: their upright bounding box), for the label to follow
    const rs = t.nodes().map(x => x.getClientRect());
    const x0 = Math.min(...rs.map(r => r.x)), y0 = Math.min(...rs.map(r => r.y));
    setLive({ rect: { x: x0, y: y0, w: Math.max(...rs.map(r => r.x + r.width)) - x0, h: Math.max(...rs.map(r => r.y + r.height)) - y0 }, text });
    if (moving !== 'transform') setMoving('transform');
  };
  /** several copies being resized together: their combined size (mm) */
  const groupSize = (nodes: Konva.Node[]): [number, number] => {
    const rs = nodes.map(x => x.getClientRect());
    const x0 = Math.min(...rs.map(r => r.x)), y0 = Math.min(...rs.map(r => r.y));
    return [(Math.max(...rs.map(r => r.x + r.width)) - x0) / view.scale, (Math.max(...rs.map(r => r.y + r.height)) - y0) / view.scale];
  };
  const onTransformEnd = () => {
    const t = tr.current;
    if (!t || !sheet) return;
    const changes = new Map<string, { cx: number; cy: number; w: number; h: number; rot: number }>();
    for (const n of t.nodes()) {
      const it = sheet.items.find(i => cssId(i.id) === n.id());
      if (!it) continue;
      const fx = Math.abs(n.scaleX()), fy = Math.abs(n.scaleY());
      changes.set(it.id, { cx: n.x(), cy: n.y(), w: it.widthMm * fx, h: it.heightMm * fy, rot: ((n.rotation() % 360) + 360) % 360 });
      n.scaleX(it.flipX ? -1 : 1);
      n.scaleY(it.flipY ? -1 : 1);
    }
    setHud(null);
    setLive(null);
    setMoving(null);
    setGuides({ v: [], h: [] });
    setEdgeFlash(false);
    const rotated = [...changes.entries()].some(([id, c]) => Math.abs(c.rot - (sheet.items.find(i => i.id === id)!.rotation % 360 + 360) % 360) > 0.01);
    commit(rotated ? 'Rotate' : 'Resize', doc => {
      const sh = doc.sheets[sheetIndex];
      sh.items.forEach(it => {
        const c = changes.get(it.id);
        if (!c) return;
        // inside the printable area (turned copies are checked here). A resize that ends up too big is made
        // smaller to fit; a turn only moves it (anything still too big shows in the checks)
        const next = { ...it, cx: c.cx, cy: c.cy, widthMm: Math.max(3, Math.round(c.w * 100) / 100), heightMm: Math.max(3, Math.round(c.h * 100) / 100), rotation: Math.round(c.rot * 100) / 100 };
        const p = rotated ? { ...next, ...clampInside(next, sh, maxLen) } : fitInside(next, sh, maxLen);
        it.widthMm = p.widthMm; it.heightMm = p.heightMm; it.rotation = p.rotation; it.cx = p.cx; it.cy = p.cy;
      });
    });
  };

  /* --------------------------------------------- stage pointer: pan, marquee */
  const pan = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  const pinch = useRef<{ d: number; cx: number; cy: number; v: View } | null>(null);

  const toWorld = (p: { x: number; y: number }) => ({ x: (p.x - view.x) / view.scale, y: (p.y - view.y) / view.scale });

  const onStageDown = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    const st = stage.current!;
    const evt = e.evt as MouseEvent & TouchEvent;
    if (evt.touches && evt.touches.length === 2) {
      const [a, b] = [evt.touches[0], evt.touches[1]];
      const r = wrap.current!.getBoundingClientRect();
      pinch.current = { d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), cx: (a.clientX + b.clientX) / 2 - r.left, cy: (a.clientY + b.clientY) / 2 - r.top, v: view };
      pan.current = null;
      return;
    }
    if ('button' in evt && evt.button === 2) return;
    const p = st.getPointerPosition();
    if (!p) return;
    const onEmpty = e.target === st || e.target.name() === 'film' || e.target.name() === 'bg';
    const middle = 'button' in evt && evt.button === 1;
    if (space.current || middle || panMode || (onEmpty && (mode === 'view' || 'touches' in evt))) {
      pan.current = { x: p.x, y: p.y, vx: view.x, vy: view.y };
      setPanning(true);
      return;
    }
    if (onEmpty && mode === 'edit') {
      if (!(evt.shiftKey || evt.ctrlKey || evt.metaKey)) clearSelection();
      const w = toWorld(p);
      setMarquee({ x0: w.x, y0: w.y, x1: w.x, y1: w.y });
    }
  };
  const onStageMove = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    const evt = e.evt as MouseEvent & TouchEvent;
    if (pinch.current && evt.touches && evt.touches.length === 2) {
      evt.preventDefault();
      const [a, b] = [evt.touches[0], evt.touches[1]];
      const r = wrap.current!.getBoundingClientRect();
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const cx = (a.clientX + b.clientX) / 2 - r.left, cy = (a.clientY + b.clientY) / 2 - r.top;
      const p0 = pinch.current;
      const s = Math.max(0.03, Math.min(60, p0.v.scale * d / p0.d));
      const wx = (p0.cx - p0.v.x) / p0.v.scale, wy = (p0.cy - p0.v.y) / p0.v.scale;
      setView({ scale: s, x: cx - wx * s, y: cy - wy * s });
      return;
    }
    const p = stage.current!.getPointerPosition();
    if (!p) return;
    if (pan.current) {
      const q = pan.current;
      setView(v => ({ ...v, x: q.vx + (p.x - q.x), y: q.vy + (p.y - q.y) }));
      return;
    }
    if (marquee) { const w = toWorld(p); setMarquee(m => (m ? { ...m, x1: w.x, y1: w.y } : m)); }
  };
  const onStageUp = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    pinch.current = null;
    if (pan.current) { pan.current = null; setPanning(false); return; }
    if (marquee && sheet) {
      const x0 = Math.min(marquee.x0, marquee.x1), x1 = Math.max(marquee.x0, marquee.x1);
      const y0 = Math.min(marquee.y0, marquee.y1), y1 = Math.max(marquee.y0, marquee.y1);
      if (x1 - x0 > 1 || y1 - y0 > 1) {
        const hit = sheet.items.filter(it => { const b = itemBox(it); return b.x < x1 && b.x + b.w > x0 && b.y < y1 && b.y + b.h > y0; }).map(i => i.id);
        const evt = e.evt as MouseEvent;
        select(hit, !!(evt.shiftKey || evt.ctrlKey || evt.metaKey));
      }
      setMarquee(null);
    }
  };

  /* -------------------------------------------------------- drop from library */
  const onDrop = (e: React.DragEvent) => {
    const id = e.dataTransfer.getData('application/x-gsb-design');
    if (!id || !sheet || mode !== 'edit') return;
    e.preventDefault();
    const r = wrap.current!.getBoundingClientRect();
    const w = toWorld({ x: e.clientX - r.left, y: e.clientY - r.top });
    void addCopy(id, { cx: w.x, cy: w.y });
  };

  /* ------------------------------------------- right-click and long-press (0.3e) */
  const itemAt = (node: Konva.Node | null): string | null => {
    for (let n: Konva.Node | null = node; n; n = n.getParent()) {
      const id = n.id();
      if (id && id.startsWith('it_')) return sheet?.items.find(i => cssId(i.id) === id)?.id || null;
    }
    return null;
  };
  const onContextMenu = (e: Konva.KonvaEventObject<PointerEvent>) => {
    if (mode !== 'edit' || panMode || space.current) return;
    const id = itemAt(e.target);
    if (!id) return;                                   // empty film: the browser's own menu
    e.evt.preventDefault();
    if (press.current || Date.now() - lastLongPress.current < 1500) return;   // a finger: the long press handles it
    if (!useStore.getState().selection.includes(id)) select([id]);
    setMenu({ x: e.evt.clientX, y: e.evt.clientY });
  };
  function cancelPress() { if (press.current) { clearTimeout(press.current.timer); press.current = null; } }
  const startPress = (id: string, evt: TouchEvent) => {
    cancelPress();
    if (mode !== 'edit' || panMode || evt.touches.length !== 1) return;
    const t = evt.touches[0];
    const timer = window.setTimeout(() => {
      const p = press.current;
      press.current = null;
      if (!p) return;
      lastLongPress.current = Date.now();
      // a long press, not a drag: put back anything the finger nudged, then show the actions
      const d = drag.current, st = stage.current;
      if (d && st) d.start.forEach((q, x) => st.findOne('#' + cssId(x))?.position(q));
      st?.find('Group').forEach(n => { if (n.isDragging()) n.stopDrag(); });
      if (!useStore.getState().selection.includes(p.id)) select([p.id]);
      setMenu({ x: p.x, y: p.y });
    }, 550);
    press.current = { id, x: t.clientX, y: t.clientY, timer };
  };
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const move = (e: TouchEvent) => { const p = press.current, t = e.touches[0]; if (p && t && Math.hypot(t.clientX - p.x, t.clientY - p.y) > 5) cancelPress(); };
    const end = () => cancelPress();
    el.addEventListener('touchmove', move, { passive: true });
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);
    return () => { el.removeEventListener('touchmove', move); el.removeEventListener('touchend', end); el.removeEventListener('touchcancel', end); cancelPress(); };
  }, []);

  // stable handlers so unchanged items never re-render
  const hRef = useRef<any>({});
  hRef.current = {
    select: (id: string, add: boolean) => (add ? toggleSelect(id) : selection.includes(id) ? null : select([id])),
    dragStart: onItemDragStart, dragMove: onItemDragMove, dragEnd: onItemDragEnd, press: startPress
  };
  const ev = useMemo<ItemEvents>(() => ({
    select: (id, add) => hRef.current.select(id, add),
    dragStart: (id, e) => hRef.current.dragStart(id, e),
    dragMove: (id, e) => hRef.current.dragMove(id, e),
    dragEnd: () => hRef.current.dragEnd(),
    press: (id, e) => hRef.current.press(id, e)
  }), []);

  // the selected copy's own print size (what the 300 DPI file uses), turned ones with their angle;
  // several: the size of their combined outline
  const sizeLabel = (() => {
    if (!selOnSheet.length || !selBox) return '';
    if (selOnSheet.length === 1) {
      const it = selOnSheet[0], r = ((it.rotation % 360) + 360) % 360;
      return formatDims(it.widthMm, it.heightMm, unit) + (r > 0.05 && r < 359.95 ? ' · ' + Math.round(r * 10) / 10 + '°' : '');
    }
    return formatDims(selBox.w, selBox.h, unit) + ' · ' + selOnSheet.length + ' selected';
  })();

  if (!sheet) return <div className="stage" ref={wrap} />;

  const bgColor = background === 'dark' ? '#1B2537' : background === 'white' ? '#FFFFFF' : undefined;
  const editing = mode === 'edit' && !panMode;
  const tile = checkerTile();
  const line = 1 / view.scale;
  const fs = 12 / view.scale;
  const L = sheet.lengthMm;

  return (
    <div className={'stage' + (panning ? ' is-panning' : '') + (panMode || spaceHeld ? ' is-pan' : '') + (rulers ? ' has-rulers' : '') + (background === 'dark' ? ' on-dark' : '')} ref={wrap} onDragOver={e => { if (mode === 'edit' && e.dataTransfer.types.includes('application/x-gsb-design')) e.preventDefault(); }} onDrop={onDrop}>
      {size.w > 0 && (
        <Stage ref={stage} width={size.w} height={size.h} scaleX={view.scale} scaleY={view.scale} x={view.x} y={view.y}
          onMouseDown={onStageDown} onTouchStart={onStageDown} onMouseMove={onStageMove} onTouchMove={onStageMove} onMouseUp={onStageUp} onTouchEnd={onStageUp}
          onContextMenu={onContextMenu}
          onMouseLeave={() => { if (pan.current) { pan.current = null; setPanning(false); } }}>
          <Layer listening={true}>
            {/* the film */}
            <Rect name="bg" x={-10000} y={-10000} width={20000 + sheet.widthMm} height={20000 + L} fill="rgba(0,0,0,0)" />
            <Rect name="film" x={0} y={0} width={sheet.widthMm} height={L}
              {...(bgColor ? { fill: bgColor } : { fillPatternImage: tile as unknown as HTMLImageElement, fillPatternScale: { x: 1 / view.scale, y: 1 / view.scale }, fillPatternRepeat: 'repeat' })}
              stroke="#C5D4DC" strokeWidth={line}
              shadowColor="#0B1220" shadowBlur={22 / view.scale} shadowOpacity={0.16} shadowOffsetY={6 / view.scale} />
            {!clean && mode === 'edit' && snap.grid && (
              <Shape name="grid" listening={false} stroke="rgba(11,18,32,.07)" strokeWidth={line}
                sceneFunc={(ctx, shape) => {
                  const g = snap.gridMm;
                  ctx.beginPath();
                  for (let x = g; x < sheet.widthMm; x += g) { ctx.moveTo(x, 0); ctx.lineTo(x, L); }
                  for (let y = g; y < L; y += g) { ctx.moveTo(0, y); ctx.lineTo(sheet.widthMm, y); }
                  ctx.strokeShape(shape);
                }} />
            )}
            {!clean && (edgeGuide || edgeFlash) && <Rect name="edge-guide" x={edgeMm} y={edgeMm} width={sheet.widthMm - 2 * edgeMm} height={L - 2 * edgeMm} stroke="rgba(8,145,165,.6)" strokeWidth={line} dash={[6 / view.scale, 5 / view.scale]} listening={false} />}
            {!clean && <Text x={0} y={-fs * 1.9} width={sheet.widthMm} align="center" text={formatLength(sheet.widthMm, unit === 'mm' ? 'cm' : unit)} fontSize={fs} fontStyle="700" fontFamily="Montserrat, sans-serif" fill="#52606D" listening={false} />}
            {!clean && <Text x={-fs * 1.9} y={L} width={L} rotation={-90} align="center" text={formatLength(L, unit === 'mm' ? 'cm' : unit)} fontSize={fs} fontStyle="700" fontFamily="Montserrat, sans-serif" fill="#52606D" listening={false} />}
          </Layer>
          <Layer>
            {sheet.items.map(it => {
              const d = designMap.get(it.designId);
              return (
                <ItemNode key={it.id} it={it} design={d} asset={d ? assets[d.assetId] : undefined} scale={view.scale}
                  editable={editing && !it.locked} selected={selection.includes(it.id)} mode={editing ? 'edit' : 'view'} ev={ev} />
              );
            })}
          </Layer>
          <Layer>
            {/* subtle checks: a small dot on a copy that needs a look, and a soft ring where printed artwork
                actually meets or comes too close (no boxes drawn around the designs) */}
            {flagged && !clean && sheet.items.filter(it => flagged.has(it.id)).map(it => {
              const b = itemBox(it);
              const col = flagged.get(it.id) === 'error' ? '#DC2626' : '#D97706';
              const r = 5 / view.scale, inset = 7 / view.scale;
              return <Circle key={'f' + it.id} name="flag" x={b.x + b.w - inset} y={b.y + inset} radius={r} fill={col} stroke="#FFFFFF" strokeWidth={1.5 / view.scale} listening={false} />;
            })}
            {spots && !clean && spots.map((p, i) => (
              <Circle key={'s' + i} name="spot" x={p.x} y={p.y} radius={11 / view.scale} fill={p.level === 'error' ? 'rgba(220,38,38,.16)' : 'rgba(217,119,6,.16)'}
                stroke={p.level === 'error' ? 'rgba(220,38,38,.9)' : 'rgba(217,119,6,.9)'} strokeWidth={2 / view.scale} dash={[4 / view.scale, 3 / view.scale]} listening={false} />
            ))}
            {selOnSheet.filter(i => i.locked).map(it => { const b = itemBox(it); return <Rect key={'l' + it.id} x={b.x} y={b.y} width={b.w} height={b.h} stroke="#11C5D9" strokeWidth={1.5 / view.scale} dash={[2 / view.scale, 3 / view.scale]} listening={false} />; })}
            {guides.v.map((x, i) => <Line key={'v' + i} points={[x, -2000, x, L + 2000]} stroke="#FF3D9A" strokeWidth={line} listening={false} />)}
            {guides.h.map((y, i) => <Line key={'h' + i} points={[-2000, y, sheet.widthMm + 2000, y]} stroke="#FF3D9A" strokeWidth={line} listening={false} />)}
            {guides.label && <Text x={guides.label.x + 4 / view.scale} y={guides.label.y - fs} text={guides.label.text} fontSize={fs} fontStyle="700" fill="#FF3D9A" listening={false} />}
            {marquee && <Rect x={Math.min(marquee.x0, marquee.x1)} y={Math.min(marquee.y0, marquee.y1)} width={Math.abs(marquee.x1 - marquee.x0)} height={Math.abs(marquee.y1 - marquee.y0)} fill="rgba(17,197,217,.1)" stroke="#11C5D9" strokeWidth={line} listening={false} />}
            {mode === 'edit' && (
              <Transformer ref={tr} rotateEnabled={!panMode} resizeEnabled={!panMode} keepRatio={keepRatioNow()} shiftBehavior="none"
                enabledAnchors={single ? ['top-left', 'top-center', 'top-right', 'middle-right', 'bottom-right', 'bottom-center', 'bottom-left', 'middle-left'] : ['top-left', 'top-right', 'bottom-left', 'bottom-right']}
                rotationSnaps={[0, 90, 180, 270]} rotationSnapTolerance={6} anchorSize={coarse ? 18 : 10} anchorCornerRadius={coarse ? 5 : 3}
                borderStroke="#11C5D9" anchorStroke="#0891A5" anchorFill="#FFFFFF" borderStrokeWidth={1.5} rotateAnchorOffset={26}
                ignoreStroke flipEnabled={false}
                boundBoxFunc={boundBox}
                onTransform={onTransform} onTransformEnd={onTransformEnd} />
            )}
          </Layer>
        </Stage>
      )}
      {rulers && size.w > 0 && <Rulers view={view} size={size} unit={unit} sheet={{ widthMm: sheet.widthMm, lengthMm: L }} sel={selBox} />}
      {hud && <div className="stage-hud tc"><span className="hud-chip num">{hud}</span></div>}
      {mode === 'edit' && !clean && selBox && sizeLabel && !marquee && size.w > 0 && (
        <SelectionOverlay
          rect={live?.rect || { x: view.x + selBox.x * view.scale, y: view.y + selBox.y * view.scale, w: selBox.w * view.scale, h: selBox.h * view.scale }}
          label={live?.text || sizeLabel} moving={!!moving} size={size} rulers={rulers} coarse={coarse}
          toolbar={!phone && !panMode && !panning && !menu} rot={moving ? null : rotPt} />
      )}
      {menu && createPortal(<SelectionMenu at={menu} phone={phone} onClose={() => setMenu(null)} />, document.body)}
      {showMinimap && sheet.lengthMm > sheet.widthMm * 2.2 && size.h > 300 && <Minimap sheet={sheet} view={view} size={size} onJump={y => setView(v => ({ ...v, y: size.h / 2 - y * v.scale }))} />}
    </div>
  );
}

function isTyping(e: KeyboardEvent) {
  const t = e.target as HTMLElement;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
}
/** macOS, where Ctrl+click means right-click */
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export function cssId(id: string) { return 'it_' + id.replace(/[^a-z0-9_-]/gi, ''); }

interface ItemEvents {
  select: (id: string, add: boolean) => void;
  press: (id: string, e: TouchEvent) => void;
  dragStart: (id: string, e: Konva.KonvaEventObject<DragEvent>) => void;
  dragMove: (id: string, e: Konva.KonvaEventObject<DragEvent>) => void;
  dragEnd: () => void;
}

interface ItemProps {
  it: Item; design?: Design; asset?: AssetInfo; scale: number; editable: boolean; selected: boolean; mode: 'edit' | 'view'; ev: ItemEvents;
}

const ItemNode = memo(function ItemNode({ it, design, asset, scale, editable, selected, mode, ev }: ItemProps) {
  const big = it.widthMm * scale > 240 || it.heightMm * scale > 240;
  const img = useAssetImage(asset, big);
  const w = it.widthMm, h = it.heightMm;
  // a design that keeps its empty space: draw the trimmed artwork where it sits in the full box
  const t = design && !design.trim && asset ? asset.trim : { x: 0, y: 0, w: 1, h: 1 };
  return (
    <Group id={cssId(it.id)} x={it.cx} y={it.cy} rotation={it.rotation} scaleX={it.flipX ? -1 : 1} scaleY={it.flipY ? -1 : 1}
      draggable={editable} listening={mode === 'edit'}
      onMouseDown={e => { if (mode !== 'edit') return; e.cancelBubble = true; ev.select(it.id, e.evt.button !== 2 && (e.evt.shiftKey || e.evt.metaKey || (e.evt.ctrlKey && !IS_MAC))); }}
      onTouchStart={e => { if (mode !== 'edit') return; e.cancelBubble = true; ev.select(it.id, false); ev.press(it.id, e.evt); }}
      onDragStart={e => ev.dragStart(it.id, e)} onDragMove={e => ev.dragMove(it.id, e)} onDragEnd={() => ev.dragEnd()}
      onMouseEnter={e => { if (editable) (e.target.getStage()!.container().style.cursor = 'move'); }}
      onMouseLeave={e => { e.target.getStage()!.container().style.cursor = ''; }}>
      <Rect x={-w / 2} y={-h / 2} width={w} height={h} fill={img ? 'rgba(0,0,0,0)' : 'rgba(17,197,217,.12)'} stroke={img ? undefined : 'rgba(8,145,165,.45)'} strokeWidth={1 / scale} />
      {img && <KImage image={img} x={-w / 2 + t.x * w} y={-h / 2 + t.y * h} width={t.w * w} height={t.h * h} perfectDrawEnabled={false} listening={false} />}
      {selected && mode === 'edit' && <Rect x={-w / 2} y={-h / 2} width={w} height={h} stroke="rgba(17,197,217,.9)" strokeWidth={1 / scale} listening={false} />}
    </Group>
  );
});

function Minimap({ sheet, view, size, onJump }: { sheet: Sheet; view: View; size: { w: number; h: number }; onJump: (yMm: number) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const W = 68, scale = W / sheet.widthMm, H = Math.min(Math.round(sheet.lengthMm * scale), Math.round(size.h * 0.55));
  const s = H / sheet.lengthMm;
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = W * dpr; c.height = H * dpr;
    const x = c.getContext('2d')!;
    x.scale(dpr, dpr);
    x.fillStyle = '#FFFFFF'; x.fillRect(0, 0, W, H);
    x.fillStyle = '#11C5D9';
    for (const it of sheet.items) { const b = itemBox(it); x.fillRect(b.x * scale, b.y * s, Math.max(1, b.w * scale), Math.max(1, b.h * s)); }
    const vy = -view.y / view.scale, vh = size.h / view.scale;
    x.strokeStyle = '#0B1220'; x.lineWidth = 1.5;
    x.strokeRect(0.75, Math.max(0.75, vy * s), W - 1.5, Math.min(H - 1.5, vh * s));
  }, [sheet, view, size.h, H]);
  return (
    <button className="minimap" aria-label="Jump to part of the sheet" onClick={e => { const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); onJump((e.clientY - r.top - 4) / s); }}>
      <canvas ref={ref} style={{ width: W, height: H }} />
    </button>
  );
}

export { preload, Icon };
